import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  IconArrowRight, IconCirclesRelation, IconBook, IconWaveSine,
  IconCompass, IconAward, IconInfinity, IconChartBar,
} from "@tabler/icons-react";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import { useI18n } from "@/contexts/I18nContext";

const MOD_ICONS = {
  circulo: <IconCirclesRelation size={22} />,
  aula: <IconBook size={22} />,
  sintonia: <IconWaveSine size={22} />,
  brujula: <IconCompass size={22} />,
  sello: <IconAward size={22} />,
  observatorio: <IconChartBar size={22} />,
};

export default function SiteHome() {
  const { t, lang } = useI18n();
  const nav = useNavigate();

  const mods = ["circulo", "aula", "sintonia", "brujula", "sello", "observatorio"];

  return (
    <div className="bg-paper">
      <SiteNav />
      {/* HERO */}
      <section className="relative overflow-hidden noise-bg">
        <div className="wrap py-24 md:py-32 relative z-10 grid md:grid-cols-[1.15fr_.85fr] gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <p className="eyebrow mb-4">{t("site.hero_eyebrow")}</p>
            <h1 className="font-display font-medium leading-[1.05] text-[clamp(38px,6vw,64px)] text-ink">
              {t("site.hero_title")}
            </h1>
            <p className="text-muted mt-6 text-[16px] max-w-[36em] leading-relaxed">
              {t("site.hero_lead")}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <button className="btn" onClick={() => nav("/login")} data-testid="site-hero-cta-enter">
                {t("site.hero_cta")} <IconArrowRight size={16} />
              </button>
              <a href="#modules" className="btn-ghost btn" data-testid="site-hero-cta-modules">
                {t("site.hero_cta2")}
              </a>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="hidden md:block relative"
          >
            <div className="relative rounded-2xl bg-teal-soft border border-line flex items-center justify-center overflow-hidden py-6">
              <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 30% 40%, rgba(14,91,73,0.25), transparent 60%)" }} />
              <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 70% 60%, rgba(194,135,44,0.4), transparent 60%)" }} />
              <svg
                viewBox="0 0 400 360"
                role="img"
                aria-label={lang === "es"
                  ? "El niño en el centro, conectado con su familia, su escuela, su psicólogo y su docente."
                  : "The child at the center, connected to family, school, psychologist and teacher."}
                className="relative w-[85%] max-w-[440px] h-auto"
              >
                {/* connections */}
                <line x1="200" y1="185" x2="200" y2="70" stroke="#0E5B49" strokeWidth="1.2" opacity=".5" />
                <line x1="200" y1="185" x2="320" y2="185" stroke="#0E5B49" strokeWidth="1.2" opacity=".5" />
                <line x1="200" y1="185" x2="200" y2="300" stroke="#0E5B49" strokeWidth="1.2" opacity=".5" />
                <line x1="200" y1="185" x2="80" y2="185" stroke="#0E5B49" strokeWidth="1.2" opacity=".5" />
                {/* outer nodes */}
                <circle cx="200" cy="70" r="8" fill="#fff" stroke="#0E5B49" strokeWidth="1.5" />
                <circle cx="320" cy="185" r="8" fill="#fff" stroke="#0E5B49" strokeWidth="1.5" />
                <circle cx="200" cy="300" r="8" fill="#fff" stroke="#0E5B49" strokeWidth="1.5" />
                <circle cx="80" cy="185" r="8" fill="#fff" stroke="#0E5B49" strokeWidth="1.5" />
                {/* pulsating center */}
                <circle cx="200" cy="185" r="16" fill="none" stroke="#C2872C" strokeWidth="1.5">
                  <animate attributeName="r" values="16;42" keyTimes="0;1" dur="1.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.4;0" keyTimes="0;1" dur="1.5s" repeatCount="indefinite" />
                </circle>
                <circle cx="200" cy="185" r="26" fill="none" stroke="#C2872C" strokeWidth="1" opacity=".4" />
                <circle cx="200" cy="185" r="15" fill="#C2872C">
                  <animate
                    attributeName="r"
                    values="15;18;15.5;17.5;15;15"
                    keyTimes="0;0.07;0.14;0.22;0.32;1"
                    dur="1.5s"
                    repeatCount="indefinite"
                    calcMode="spline"
                    keySplines="0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1"
                  />
                </circle>
                {/* labels */}
                <text x="200" y="50" textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="11" fill="#5C5F55">{lang === "es" ? "familia" : "family"}</text>
                <text x="338" y="189" fontFamily="IBM Plex Mono, monospace" fontSize="11" fill="#5C5F55">{lang === "es" ? "escuela" : "school"}</text>
                <text x="200" y="324" textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="11" fill="#5C5F55">{lang === "es" ? "psicólogo" : "psychologist"}</text>
                <text x="62" y="189" textAnchor="end" fontFamily="IBM Plex Mono, monospace" fontSize="11" fill="#5C5F55">{lang === "es" ? "docente" : "teacher"}</text>
                <text x="200" y="189" textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="11" fill="#fff">{lang === "es" ? "niño" : "child"}</text>
              </svg>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-20 border-t border-line">
        <div className="wrap">
          <div className="mb-10">
            <p className="eyebrow">{t("site.pillars_eyebrow")}</p>
            <h2 className="font-display font-medium text-[clamp(26px,4vw,36px)] mt-2">
              {t("site.pillars_title")}
            </h2>
          </div>
          <div className="grid lg:grid-cols-[1.5fr_1fr] gap-8 items-start">
            {/* Left column — the two pillar cards + infinity connector */}
            <div className="grid sm:grid-cols-2 gap-6 relative">
              <div className="hidden sm:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 items-center justify-center">
                <span className="w-14 h-14 rounded-full bg-honey text-white flex items-center justify-center shadow-lg">
                  <IconInfinity size={26} />
                </span>
              </div>
              <div className="mod-card">
                <div className="font-display text-4xl text-teal-deep opacity-70 mb-2">01</div>
                <h3 className="font-display text-[22px] font-medium">{t("site.pillar1_title")}</h3>
                <p className="text-muted text-[14px] mt-3 leading-relaxed">{t("site.pillar1_desc")}</p>
              </div>
              <div className="mod-card">
                <div className="font-display text-4xl text-teal-deep opacity-70 mb-2">02</div>
                <h3 className="font-display text-[22px] font-medium">{t("site.pillar2_title")}</h3>
                <p className="text-muted text-[14px] mt-3 leading-relaxed">{t("site.pillar2_desc")}</p>
              </div>
            </div>
            {/* Right column — Dra. Mariela welcome video */}
            <aside className="mod-card p-3 sm:p-4 max-w-md mx-auto lg:mx-0 w-full" data-testid="welcome-video-card">
              <p className="eyebrow mb-2 text-[10px]">
                {lang === "es" ? "Bienvenida" : "Welcome"}
              </p>
              <div className="relative w-full max-w-full aspect-[9/16] rounded-lg overflow-hidden bg-black">
                <video
                  className="absolute inset-0 w-full h-full object-cover"
                  controls
                  playsInline
                  {...{"webkit-playsinline": "true"}}
                  preload="metadata"
                  controlsList="nodownload noremoteplayback"
                  onContextMenu={(e) => e.preventDefault()}
                  data-testid="welcome-video"
                >
                  <source src="/media/bienvenida-mariela.mp4" type="video/mp4" />
                </video>
              </div>
              <p className="text-muted text-[12.5px] mt-3 leading-snug">
                {lang === "es"
                  ? "Dra. Mariela Chiessa · Fundadora"
                  : "Dr. Mariela Chiessa · Founder"}
              </p>
            </aside>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section id="modules" className="py-20 bg-white border-t border-line">
        <div className="wrap">
          <div className="mb-10">
            <p className="eyebrow">{t("site.mods_eyebrow")}</p>
            <h2 className="font-display font-medium text-[clamp(26px,4vw,36px)] mt-2">
              {t("site.mods_title")}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {mods.map((m, i) => (
              <motion.div
                key={m}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`mod-card ${m === "observatorio" ? "bg-teal-soft border-teal-soft" : ""}`}
                data-testid={`site-module-${m}`}
                onClick={() => nav("/login")}
              >
                <div className="flex items-center gap-2 mb-3 text-teal-deep">
                  {MOD_ICONS[m]}
                </div>
                <span className="tag">{t(`modules.${m}.name`)}</span>
                <h3 className="font-display text-[20px] font-medium mt-2">{t(`modules.${m}.who`)}</h3>
                <p className="text-muted text-[13.5px] mt-2 leading-relaxed">{t(`modules.${m}.desc`)}</p>
                {m !== "observatorio" && (
                  <span className="inline-flex items-center gap-1 text-teal text-[12px] mt-4 font-medium">
                    {t("site.explore_module")} <IconArrowRight size={13} />
                  </span>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="py-20 border-t border-line">
        <div className="wrap">
          <div className="mb-10">
            <p className="eyebrow">{t("site.why_eyebrow")}</p>
            <h2 className="font-display font-medium text-[clamp(26px,4vw,36px)] mt-2">
              {t("site.why_title")}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[1, 2, 3].map((n) => (
              <div key={n}>
                <h3 className="font-display text-[18px] font-medium flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-honey" />
                  {t(`site.why${n}_title`)}
                </h3>
                <p className="text-muted text-[14px] mt-3 leading-relaxed">{t(`site.why${n}_desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA DARK */}
      <section className="py-24 border-t border-line" style={{ background: "#0A3F33" }}>
        <div className="wrap text-center text-white">
          <p className="eyebrow" style={{ color: "#C2872C" }}>{t("site.cta_eyebrow")}</p>
          <h2 className="font-display font-medium text-[clamp(28px,4vw,42px)] mt-3 max-w-[22ch] mx-auto leading-tight">
            {t("site.cta_title")}
          </h2>
          <p className="text-[15px] mt-5 opacity-85 max-w-[36em] mx-auto">{t("site.cta_desc")}</p>
          <a
            href="mailto:info@chiessainstitute.com"
            className="btn mt-8"
            style={{ background: "#C2872C", borderColor: "#C2872C" }}
            data-testid="site-cta-mail"
          >
            {t("site.cta_btn")}
          </a>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
